import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSessionPlan, useSessionProgress } from "@/features/sessions";
import { Timer, pieceFromPlanBlock } from "@/features/timer";

/**
 * THE CLOCK — pushed from the session, running THAT session's conditioning piece.
 *
 * 🚨 THIS ROUTE USED TO HAND OVER NOTHING BUT AN ID, and that single omission was the whole of
 * two reported bugs. BUD-285 built the two layouts and the function that turns a block into a
 * piece; the BLOCK had no source, so the timer fell through to `OPEN_PIECE` — an honest
 * stopwatch with no movements, no rounds and `paced: 'athlete'`. What an athlete saw was a
 * timer with no metcon in it, and an EMOM that looked exactly like an AMRAP because
 * `layoutFor` reads `paced` and every piece it was given declared the same one.
 *
 * The session screen's loader exists now (BUD-287), so the plan is read here the same way that
 * screen reads it — the seed and gear this phone wrote when the deck committed — and the
 * conditioning block is handed over as a real piece. Everything downstream was already right.
 *
 * ⚠️ THE PIECE IS UNDEFINED UNTIL THE PLAN LANDS, and that is deliberate rather than a gap.
 * `Timer` defaults to the open stopwatch, which is the honest thing to show for the fraction of
 * a second the draw is in the air; the clock is not started by arriving, so nothing can be
 * timed against the placeholder. A session with no conditioning block — a pure strength day —
 * keeps the stopwatch for good, which is the right answer rather than an empty screen.
 *
 * FULL-BLEED IS THE SCREEN'S OWN DOING. It cancels `SCREEN_INSET` and the shell's top padding
 * rather than changing the transition, which twenty other screens depend on. The floating tab
 * bar is gone from this route as well — see `fullBleed` in `_app.tsx` — because the way back
 * the shell's rule asks for in exchange is already on this screen: the ✕ above, and the
 * footer control's hold below.
 */
export const Route = createFileRoute("/_app/session/$id/timer")({
  component: TimerScreen,
});

/** The block with the clock on it. The engine never repeats it and a session may omit it. */
const CONDITIONING = "conditioning";

function TimerScreen() {
  const { id } = Route.useParams();
  const navigate = useNavigate();

  const progress = useSessionProgress(id);
  const plan = useSessionPlan(id, progress.pointer);

  const session = plan.data ?? null;
  const block = session?.blocks.find((one) => one.id === CONDITIONING) ?? null;
  /* THE TITLE IS THE SESSION'S, not the block's — a block has no name of its own, and the
     header of a running piece is where an athlete confirms they are in the right workout. */
  const piece =
    block === null ? undefined : pieceFromPlanBlock(block, session?.format ?? null, session?.title ?? null);

  return (
    <Timer
      sessionId={id}
      piece={piece}
      /**
       * 🚨 THE PIECE STARTING IS THE SESSION STARTING, and this is the one path where nothing
       * else would say so.
       *
       * The session begins on an act — the detail screen's own `Start …`, a block ticked off, a
       * set logged (see `beginSession` in `use-session-progress.ts`). An athlete who taps the
       * metcon tile straight from the list and runs the clock performs NONE of those until the
       * piece is already over, so the session would be filed as beginning the moment they
       * finished it: a whole metcon at zero elapsed seconds.
       *
       * ⚠️ NOT ON ARRIVING, which this route's header has always insisted on — reading the
       * workout you are about to do is not doing it. This fires on the 3-2-1, which is.
       */
      onStarted={progress.beginSession}
      /**
       * 🔑 TWO ENDINGS, TWO DESTINATIONS — the handoff's rule: "cap reaching zero
       * auto-finishes straight to the debrief. No confirmation." The cap running out, the
       * structure completing and a deliberate finish all produce a RESULT, and the screen
       * that exists to collect what the athlete thought of it is the debrief: the clock, the
       * rounds and the three ratings are all facts about THIS PIECE, and they are asked for
       * while it is still fresh. The ✕ produces none — it abandons the piece with nothing
       * logged — so it goes back to the session, which is where it was started from.
       *
       * ⚠️ THE DEBRIEF IS NOT THE END OF THE SESSION, and this route no longer has to know
       * anything about that. It scores the metcon, ticks it off, and hands the athlete back
       * to their session if an accessory or a cooldown is still owed — see its own `finish`.
       * A version of this route tried to make that decision HERE, by refusing to push to the
       * debrief while blocks remained; it put the score behind the cooldown, which is the
       * wrong way round for a screen whose questions are all about the piece that just ended.
       *
       * ⚠️ NEVER TO "today's" SESSION in either case. On a two-a-day that is the other session
       * entirely, so both destinations carry this route's own id.
       */
      onFinished={(outcome) =>
        void navigate(
          outcome == null
            ? { to: "/session/$id", params: { id } }
            : { to: "/session/$id/debrief", params: { id } },
        )
      }
    />
  );
}
